import { Divider } from '@design-system';
import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import { useTranslation } from 'next-i18next';
import { useContext, useEffect, useState } from 'react';
import SbEditable from 'storyblok-react';

import { SERIES_PAGE_TYPE } from '../../constants';
import { pathFor } from '../../helpers/links';
import { useIsSubscribed } from '../../hooks/subscription/useIsSubscribed';
import { useCurrency } from '../../hooks/useCurrency';
import {
  GeneratedSubscriptionType,
  useGetDrawByProductIDsQuery,
} from '../../types/generated';
import { TrackLinkClicked } from '../../utils/analytics';
import { addSlugToProducts, isUpcoming } from '../../utils/product';
import { fetchProductsByIds } from '../../utils/shopify/fetchProductsByIds';
import { NavigationStateContext } from '../Containers/NavigationStateProvider/NavigationStateProvider';
import SeriesAbstract from '../Containers/SeriesAbstract/SeriesAbstract';
import { ProductRecommendations } from '../Content/ProductRecommendations';
import Breadcrumbs from '../Layout/Breadcrumbs';
import PageSection from '../Layout/PageSection';
import ProductTile from '../ProductTile';

type Props = {
  blok: SeriesType;
  recommendations: MultiCurrencyProduct[];
};

const Series = ({ blok, recommendations }: Props) => {
  const { t } = useTranslation(['series', 'common']);
  const [currency] = useCurrency();
  const [additionalProducts, setAdditionalProducts] = useState<Product[]>(
    blok.additionalProducts,
  );
  const { navigationState } = useContext(NavigationStateContext);

  const productIDs = blok.series.map((p) => shopifyGraphqlIdToAdminId(p.id));
  const { data: draws } = useGetDrawByProductIDsQuery({
    variables: { productIDs },
    skip: productIDs.length === 0,
  });

  useEffect(() => {
    (async () => {
      const updatedAdditionalProducts = await fetchProductsByIds(
        additionalProducts.map((product) => product.id),
        currency,
        'GQL',
      );

      setAdditionalProducts(
        addSlugToProducts(updatedAdditionalProducts, additionalProducts),
      );
    })();
  }, [currency]);

  const navOptions =
    navigationState && navigationState.type === 'PRODUCT'
      ? navigationState
      : undefined;

  const breadcrumb = navOptions
    ? t('common:backToResults', { count: navOptions.count })
    : t('breadcrumbs', { count: blok.worksCount });

  const breadcrumbIndexHref = navOptions
    ? {
        pathname: pathFor({ page: 'WORKS_INDEX', pageNumber: navOptions.page }),
        query: {
          medium: navOptions.mediums,
          order: navOptions.order,
          q: navOptions.query,
          availability: navOptions.availability,
          release: navOptions.release,
          finish: navOptions.finish,
        },
      }
    : pathFor({ page: 'WORKS_INDEX' });

  const breadcrumbHref = navOptions ? '' : breadcrumbIndexHref;

  const upcomingProducts = [...blok.series, ...additionalProducts].filter(
    (product) => {
      return isUpcoming(product);
    },
  );

  const { checkIsSubscribed, refetch } = useIsSubscribed(
    upcomingProducts.map((product) => ({
      type: GeneratedSubscriptionType.Product,
      objectId: shopifyGraphqlIdToAdminId(product.id) || '',
    })),
  );

  return (
    // @ts-expect-error require storyblok-react upgrade
    <SbEditable content={blok}>
      <Breadcrumbs
        href={breadcrumbHref}
        label={breadcrumb}
        onClick={() => {
          TrackLinkClicked({
            page: SERIES_PAGE_TYPE,
            context: 'breadcrumbs',
            title: breadcrumb,
            url: navOptions
              ? pathFor({ page: 'WORKS_INDEX', pageNumber: navOptions.page })
              : (breadcrumbHref as string),
            isExternal: false,
          });
        }}
      />

      <main className="my-9 space-y-9 md:my-12 md:space-y-12">
        <PageSection>
          <section className="grid-section">
            <SeriesAbstract
              artist={blok.artist[0]}
              description={blok.description}
              summary={
                blok.series &&
                t('series:summary', {
                  seriesCount: blok.series.length,
                })
              }
              title={blok.title}
            />
          </section>
        </PageSection>

        {blok.series.length > 0 && (
          <PageSection>
            <section className="grid-section">
              {blok.series.map((product: Product, index) => {
                const draw = draws?.getDrawByProductIDs?.find((d) => {
                  return d.productId === shopifyGraphqlIdToAdminId(product.id);
                });

                return (
                  <div key={product.id} className="col-span-6 md:col-span-3">
                    <ProductTile
                      analytics={{
                        page: 'series',
                        context: 'series product',
                        position: index,
                      }}
                      draw={draw}
                      isSubscribed={checkIsSubscribed(
                        shopifyGraphqlIdToAdminId(product.id),
                        GeneratedSubscriptionType.Product,
                      )}
                      onSubscribe={() => {
                        refetch();
                      }}
                      product={product}
                    />
                  </div>
                );
              })}
            </section>
          </PageSection>
        )}

        {additionalProducts.length > 0 && (
          <PageSection>
            <section className="grid-section">
              <div className="col-span-12 pb-4">
                <h2 className="text-md text-neutral-4 md:text-lg">
                  {blok.additionalProductsTitle}
                </h2>
              </div>
              {additionalProducts.map((product: Product, index) => (
                <div key={product.id} className="col-span-6 md:col-span-3">
                  <ProductTile
                    analytics={{
                      page: 'series',
                      context: 'additional product',
                      position: index,
                    }}
                    isSubscribed={checkIsSubscribed(
                      shopifyGraphqlIdToAdminId(product.id),
                      GeneratedSubscriptionType.Product,
                    )}
                    onSubscribe={() => {
                      refetch();
                    }}
                    product={product}
                  />
                </div>
              ))}
            </section>
          </PageSection>
        )}

        <div className="wrapper-website">
          <Divider className="my-9 md:my-12" />
        </div>

        <PageSection>
          <ProductRecommendations
            analytics={{
              page: SERIES_PAGE_TYPE,
              context: 'more works',
            }}
            recommendations={recommendations}
          />
        </PageSection>
      </main>
    </SbEditable>
  );
};

export default Series;
